import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/lib/supabase'
import { consumeLoginRedirect } from '@/components/MagicLinkForm'

export function AuthCallback() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [processing, setProcessing] = useState(true)

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Supabase handles the OAuth/magic-link callback automatically
        // by reading the hash fragment from the URL.
        // We check if there's an active session after the redirect.
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession()

        if (sessionError) throw sessionError

        if (!session) {
          // No session found — might be a direct visit or expired link
          navigate('/', { replace: true })
          return
        }

        // Check if user has completed onboarding
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('onboarded_at, display_name')
          .eq('id', session.user.id)
          .single()

        if (profileError && profileError.code !== 'PGRST116') {
          // PGRST116 = no rows, which means new user
          console.warn('Profile fetch error:', profileError)
        }

        const isNewUser = !profile?.onboarded_at

        if (isNewUser) {
          // New user — send to onboarding
          navigate('/onboarding/manifesto', { replace: true })
        } else {
          // Returning user — honour a redirect stashed by the login gate
          // (/login-gate?from=...), otherwise go home.
          const redirect = consumeLoginRedirect()
          navigate(redirect ?? '/home', { replace: true })
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : t('auth.error_generic')
        setError(message)
        setProcessing(false)
      }
    }

    handleAuthCallback()
  }, [navigate, t])

  if (error) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-terra font-body text-lg mb-2">{t('auth.error_generic')}</p>
          <p className="text-ink-muted text-sm mb-6">{error}</p>
          <button
            onClick={() => navigate('/', { replace: true })}
            className="text-green hover:underline text-sm font-body"
            type="button"
          >
            {t('auth.back_to_home')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-parchment flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <img
          src="/brand/cherry-leaf-64.png"
          alt=""
          className="w-10 h-10 animate-breathe"
        />
        <p className="text-ink-muted font-body text-sm">{t('auth.signing_in')}</p>
      </div>
    </div>
  )
}
